import xor from 'lodash/xor'
import React from 'react'
import {
  ContactFragment,
  DetailedGroupFragment,
  getAllGroups,
  getPaginatedGroups,
  MinimalCampaignFragment,
} from 'src/graphql'
import {
  Alert,
  Button,
  Confirm,
  DefaultError,
  Pagination,
  ProgressModal,
  SuspenseBoundary,
  Transition,
} from 'src/chrome'
import { AddOrderCardRoute } from 'src/orders/routes/AddOrderCardRoute'
import { SearchFilter } from 'src/chrome/FilteredSearchBar/types'
import {
  useAccount,
  useActions,
  useCallback,
  useMemo,
  useMutations,
  useQueries,
  useSelector,
  useState,
} from 'src/hooks'
import { exportContactsCsvFrom } from 'src/app/api'
import {
  ContactActionBar,
  GroupAddCreateModal,
  GroupEditForm,
  GroupMembers,
  Groups,
  SelectCampaignModal,
} from '../components'
import { Group } from '../contactTypes'
import GroupHeader from '../components/GroupHeader/GroupHeader'
import styles from './contactManager.module.scss'
import { DEFAULT_GROUP_SEARCH, generateContactFilterPayload } from './filters'
import { GroupsRoute } from '../routes/GroupsRoute'
import AnnouncementBanners from 'src/marketing/AnnouncementBanners/AnnouncementBanners'
import { Step, Steps } from './types'
import { Set } from 'immutable'

const PAGE_SIZE = 20

const Hooks = () => {
  const [selected, setSelected] = useState<string[]>([])
  const [search, setSearch] = useState<{
    filters: SearchFilter[]
    terms: string
  }>(DEFAULT_GROUP_SEARCH)
  const reset = useCallback(() => setSearch(DEFAULT_GROUP_SEARCH), [])
  const [page, setPage] = useState(1)
  const [step, setStep] = useState<Step>({ type: Steps.View })
  const resetStep = () => setStep({ type: Steps.View })
  const [loading, setLoading] = useState(0)
  const actions = useActions()
  const account = useAccount()

  const [allGroups, { results: paginatedGroups }] = useQueries(
    getAllGroups(),
    getPaginatedGroups({
      search: search.terms,
      page,
      ...generateContactFilterPayload(search.filters),
    }),
  )

  const mutations = useMutations()

  const toggleSelectAll = () => {
    if (selected.length === allGroups.length) {
      setSelected([])
    } else {
      setSelected(allGroups.map(x => x.id))
    }
  }

  const toggleSelect = (group: Group) => {
    setSelected(x => xor(x, [group.id]))
  }

  const setFilters = useCallback(
    filters => setSearch(x => ({ ...x, filters })),
    [],
  )
  const setTerms = useCallback(terms => setSearch(x => ({ ...x, terms })), [])

  const editGroup = async (group: Group) => {
    setStep({ type: Steps.EditGroup, group })
  }

  const updateGroup = (group: Group) => {
    setLoading(x => x + 1)
    mutations.updateGroup({
      group: {
        id: group.id,
        name: group.name,
        description: group.description,
      },
    })
  }

  const selectionMap = useMemo(() => {
    const mutableMap: Record<string, true> = {}

    selected.forEach(x => (mutableMap[x] = true))
    return mutableMap
  }, [selected])

  const createGroup = (group: Omit<Group, 'id'>) => {
    const ids: string[] =
      group.members?.map((contact: ContactFragment | string) =>
        typeof contact == 'string' ? contact : contact.id,
      ) ?? []

    mutations.createGroup({
      group: {
        name: group.name,
        members: ids,
        description: group.description,
      },
    })
    actions.toggleIsNewGroupModalOpen()
  }

  const confirmDelete = () => setStep({ type: Steps.ConfirmDelete })

  const selectedGroups = useMemo(
    () => allGroups.filter(contact => selectionMap[contact.id]),
    [selectionMap, allGroups],
  )

  const sendCampaign = async (campaign: MinimalCampaignFragment) => {
    setLoading(x => x + 1)
    resetStep()

    try {
      const {
        createOrder: { order },
      } = await mutations.createOrder({
        order: {
          campaign: campaign.id,
          contactsFromGroups: selectedGroups.map(group => group.id),
        },
      })

      actions.createdOrder(order)
      actions.openOrder(order.id)
    } catch (error) {
      console.error(error.toString())
    } finally {
      setLoading(x => x - 1)
    }
  }

  const deleteSelectedGroups = async () => {
    selectedGroups.forEach(({ id }) => {
      mutations.deleteGroup({ id })
    })

    resetStep()
  }

  const sendCard = async (recipients?: string[]) => {
    setLoading(x => x + 1)

    try {
      const {
        createOrder: { order },
      } = await mutations.createOrder({
        order: { contactsFromGroups: selectedGroups.map(group => group.id) },
      })
      actions.openOrder(order.id, AddOrderCardRoute())
    } catch (error) {
      console.error(error.toString())
    } finally {
      setLoading(x => x - 1)
    }
  }

  const manageMembers = async ({ id }: Group) => {
    setStep({ type: Steps.ManageMembers, groupId: id })
  }

  const selectCampaign = (recipients?: string[]) => {
    setStep({
      type: Steps.SelectCampaign,
      recipients,
    })
  }

  const confirmRemoveMembers = (
    group: DetailedGroupFragment,
    removed: string[],
  ) => {
    setStep({
      type: Steps.RemoveMembers,
      confirmed: false,
      removed,
      group,
    })
  }

  const removeMembers = (group: DetailedGroupFragment, removed: string[]) => {
    mutations.updateGroup({
      group: {
        ...group,
        members: xor(
          group.members.map(member => member.id),
          removed,
        ),
      },
    })

    resetStep()
  }

  const confirmExport = useCallback(() => {
    setStep({
      type: Steps.ConfirmExport,
      email: account.email,
    })
  }, [account])

  const exportContacts = async () => {
    setStep({
      type: Steps.ExportProgress,
    })
    await exportContactsCsvFrom(
      'groups',
      Set(selectedGroups.map(group => group.id)),
    )
  }

  return {
    step,
    setStep,
    paginatedGroups,
    allGroups,
    selected,
    toggleSelectAll,
    setPage,
    setTerms,
    setFilters,
    toggleSelect,
    allSelected: selected.length === allGroups.length,
    selectionMap,
    openContact: () => {},
    viewContactHistory: () => {},
    isLoading: loading > 0,
    search,
    resetStep,
    editGroup,
    updateGroup,
    createGroup,
    confirmDelete,
    deleteSelectedGroups,
    selectedGroups,
    selectCampaign,
    sendCampaign,
    sendCard,
    manageMembers,
    confirmRemoveMembers,
    removeMembers,
    page,
    reset,
    actions,
    exportContacts,
    confirmExport,
  }
}

const GroupManagerContent: React.FC<{}> = () => {
  const hooks = Hooks()
  const { step } = hooks

  const actions = useActions()

  const route = useSelector(state => state.route)
  const isNewGroupModalOpen =
    route.subroute.path === GroupsRoute.path
      ? route.subroute.isNewGroupModalOpen
      : null

  return (
    <div className={styles.managerContainer}>
      <AnnouncementBanners />

      <GroupHeader
        onAdd={hooks.actions.toggleIsNewGroupModalOpen}
        onOpenContacts={() => actions.openRelationshipManager()}
        filters={hooks.search.filters}
        setTerms={hooks.setTerms}
        setFilters={hooks.setFilters}
        onImport={actions.openContactsImporter}
      />

      {hooks.isLoading && <Transition message="One Moment..." />}

      {isNewGroupModalOpen && (
        <GroupAddCreateModal
          isOpen={true}
          onClose={hooks.actions.toggleIsNewGroupModalOpen}
          initialMode="create"
          onCreateGroup={hooks.createGroup}
          contactsIds={[]}
          groups={[]}
        />
      )}

      {step.type === Steps.ManageMembers && (
        <GroupMembers
          onClose={hooks.resetStep}
          onRemove={hooks.confirmRemoveMembers}
          groupId={step.groupId}
        />
      )}

      {step.type === Steps.RemoveMembers &&
        (step.confirmed ? (
          <Transition message="Removing contacts..." />
        ) : (
          <Confirm
            title={'Remove contacts from group?'}
            message={'Are you sure you want to remove the selected contacts?'}
            confirmTitle={'Remove'}
            onConfirm={() => hooks.removeMembers(step.group, step.removed)}
            onDecline={hooks.resetStep}
          />
        ))}

      {step.type === Steps.EditGroup && (
        <GroupEditForm
          title={'Modify Group'}
          onClose={hooks.resetStep}
          group={step.group}
          onSubmit={hooks.updateGroup}
        />
      )}

      {step.type === Steps.SelectCampaign && (
        <SelectCampaignModal
          isOpen={true}
          onClose={hooks.resetStep}
          onSubmit={hooks.sendCampaign}
        />
      )}

      {step.type === Steps.ConfirmDelete && (
        <Confirm
          title={`Delete ${Object.keys(hooks.selectedGroups).length} groups`}
          message={'Are you sure you want to delete the selected groups?'}
          confirmTitle={'Delete'}
          onConfirm={hooks.deleteSelectedGroups}
          onDecline={hooks.resetStep}
        />
      )}

      {step.type === Steps.DeleteProgress && (
        <ProgressModal
          size={100}
          isOpen={true}
          currentCount={step.deleted}
          totalCount={Object.keys(hooks.selectedGroups).length || undefined}
          errorTitle={'The following groups could not be deleted'}
          errorList={step.errors}
          message={'groups deleted successfully'}
          onClose={step.done ? hooks.resetStep : undefined}
          isDone={step.done}
        />
      )}

      <ContactActionBar
        title="Groups"
        totalCount={hooks.allGroups.length}
        selectedCount={hooks.selected.length}
        isViewingGroups={true}
        onToggleSelectAll={hooks.toggleSelectAll}
        onSendCard={hooks.sendCard}
        onViewGroups={actions.openRelationshipManager}
        onStartCampaign={hooks.selectCampaign}
        onDelete={hooks.confirmDelete}
        onExport={hooks.confirmExport}
        onManageMembers={hooks.manageMembers}
        onEditGroup={hooks.editGroup}
        selectedGroups={hooks.selectedGroups}
      />
      <div className={styles.body}>
        {step.type === Steps.ExportProgress && (
          <Alert
            isOpen={true}
            onClose={hooks.resetStep}
            type={'info'}
            timeout={7000}
            title={'Exporting to CSV'}
            message={'Your contacts will begin downloading shortly.'}
          />
        )}

        {step.type === Steps.ConfirmExport && (
          <Confirm
            title={'Export contacts'}
            message={
              'Your CSV file will be downloaded by the browser. If you selected a large number of contacts, it will take a few seconds for the download to begin.'
            }
            confirmTitle={'Export'}
            childType={'form'}
            onConfirm={() => hooks.exportContacts()}
            onDecline={hooks.resetStep}
          />
        )}

        {hooks.paginatedGroups.length > 0 ? (
          <div className={styles.tableContainer}>
            <Groups
              isEveryGroupSelected={hooks.allSelected}
              groups={hooks.paginatedGroups}
              onSelectAll={hooks.toggleSelectAll}
              selectedGroups={hooks.selectionMap}
              onSelect={hooks.toggleSelect}
              onEditGroup={hooks.editGroup}
              onViewGroup={hooks.manageMembers}
            />
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <span>No groups found</span>

            <Button
              title={'Clear Search'}
              buttonColor={'pink'}
              style={{ marginTop: 15 }}
              onClick={hooks.reset}
            />
          </div>
        )}
      </div>
      <div className={styles.pagination}>
        <Pagination
          onPageChange={hooks.setPage}
          count={hooks.allGroups.length}
          pagesToShow={5}
          pageSize={PAGE_SIZE}
          currentPage={hooks.page}
        />
      </div>
    </div>
  )
}

const GroupManager: React.FC<{}> = props => (
  <SuspenseBoundary
    unresolved={<Transition message={'Loading groups...'} />}
    failure={DefaultError}
  >
    <GroupManagerContent {...props} />
  </SuspenseBoundary>
)

export default GroupManager
